import { useId, useMemo, useRef, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { LookupFieldSelect } from './LookupFieldSelect';
import { LookupMultiSelect } from './LookupMultiSelect';
import { findClosestMatch } from '../../utils/stringSimilarity';
import {
  canonicalRenameFor,
  resolveTypedTierName,
  tierNameOptions,
  type CalibreTierVocabulary,
  type TierNameOption,
} from '../../utils/tierVocabulary';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import type {
  CalibreData,
  CalibreImage,
  CalibreTier,
  CalibreTierOverrides,
} from '../../services/firebase';
import {
  TierImagesField,
  TierManufacturerFields,
  type TierManufacturerOption,
} from './TierMediaFields';
import {
  OVERRIDABLE_FIELDS,
  cloneTier,
  countOverrides,
  baseValueFor,
  displayOverrideValue,
  isAccuracyKey,
  formatAccuracy,
  effectiveAccuracy,
} from './calibreTiers';

interface CalibreTiersEditorProps {
  tiers: CalibreTier[];
  onChange: (tiers: CalibreTier[]) => void;
  /**
   * Current base calibre spec (derived from the live form) so each override row
   * can show the base value the tier would replace.
   */
  baseData: CalibreData;
  /** Display name for the synthetic "Base" row heading the tier list (''→"Base"). */
  baseTierName: string;
  onBaseTierNameChange: (name: string) => void;
  /**
   * The calibre's own spec values (live form), used to populate the read-only
   * readouts on the synthetic base row. Same source as {@link baseData}.
   */
  baseSpec: CalibreData;
  /**
   * The tier names that govern this calibre: its movement manufacturer's list
   * and its family's (structured + legacy). Drives the grouped name picker,
   * did-you-mean, and the "rename to canonical" hint. Absent/empty ⇒ free-text
   * names as before.
   */
  tierVocabulary?: CalibreTierVocabulary;
  /**
   * Persist a brand-new GENERIC tier name to the manufacturer's or the family's
   * list (the "add as new" path, which asks which). Awaited before the tier
   * commits, so a tier never carries a name whose save failed. Absent ⇒ new
   * names stay on this calibre only. Brand tier names are never added from here
   * — they are curated on the manufacturer / family Tier Names sections.
   */
  onAddTierName?: (destination: 'manufacturer' | 'family', name: string) => Promise<void>;
  /**
   * Brands available for a brand-specific tier's required brand picker
   * (id + name; pre-sorted by the caller). Absent/empty ⇒ the brand-specific
   * option is still selectable but the picker shows no options.
   */
  brandOptions?: Array<{ id: string; name: string }>;
  /**
   * Sensible default brand for a new brand-specific tier — typically the
   * calibre's own `brandId`. Pre-selected when the user switches a new tier to
   * brand-specific.
   */
  defaultBrandId?: string;
  /** The tier's own manufacturers & images — see {@link TierMediaProps}. */
  media?: TierMediaProps;
}

/**
 * What the tier form needs from its host to edit a tier's manufacturers and
 * images. Absent ⇒ those sections are hidden, but values already on a tier are
 * still preserved through an edit (the form never rebuilds a tier from scratch).
 */
export interface TierMediaProps {
  manufacturerOptions: TierManufacturerOption[];
  manufacturersLoading?: boolean;
  /** Upload into the calibre's image folder; undefined until the calibre has a doc id. */
  uploadImage?: (file: Blob, fileName: string) => Promise<string>;
  resolveImageUrl?: (storagePath: string) => Promise<string>;
  fetchImage?: (url: string) => Promise<Blob>;
  defaultImageSource: string;
  maxImages: number;
}

const inputClass = 'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm';

/** Generate a stable tier id. `crypto.randomUUID` is available in all supported browsers. */
function newTierId(): string {
  return crypto.randomUUID();
}

/** `editing` sentinel for the clone form — not a tier id, and not '' (add). */
const CLONE_EDIT_KEY = '__clone__';

/**
 * Build a clean overrides object containing ONLY the keys that are set — the
 * calibre save path strips undefined shallowly and won't recurse into the tier
 * objects, so leaving `undefined` keys here would make Firestore reject the
 * write. Cleared fields are simply absent.
 *
 * Exported so the ref-editor's per-ref `calibreOverrides` UI (Phase 3a) can
 * reuse the exact same sanitisation the tier editor uses — the two override
 * surfaces target the identical field set.
 */
export function cleanOverrides(draft: CalibreTierOverrides): CalibreTierOverrides {
  const out: CalibreTierOverrides = {};
  for (const f of OVERRIDABLE_FIELDS) {
    const v = draft[f.key];
    if (v === undefined) continue;
    // Empty string on a text/lookup field means "inherit" → drop it.
    if (typeof v === 'string' && v.trim() === '') continue;
    // Number fields: NaN (from an emptied numeric input) means "inherit".
    if (typeof v === 'number' && Number.isNaN(v)) continue;
    // Cast preserves the per-key value type without widening the whole object.
    (out[f.key] as CalibreTierOverrides[typeof f.key]) = v;
  }
  return out;
}

/**
 * Tri-state boolean control for a tier override: Inherit (undefined) / Yes / No.
 * Exported for reuse by the ref-editor per-ref override UI (Phase 3a).
 */
export function BooleanOverride({
  value,
  onChange,
}: {
  value: boolean | undefined;
  onChange: (v: boolean | undefined) => void;
}) {
  const selectValue = value === undefined ? '' : value ? 'true' : 'false';
  return (
    <select
      value={selectValue}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? undefined : v === 'true');
      }}
      className={inputClass}
    >
      <option value=''>Inherit</option>
      <option value='true'>Yes</option>
      <option value='false'>No</option>
    </select>
  );
}

/**
 * Grouped override control for the three accuracy keys. The two numeric bounds
 * are reinterpreted by `accuracySymmetric`, so they're edited together with an
 * Interpretation override (Inherit / Symmetric ± / Asymmetric) that relabels
 * the inputs. Each key is an INDEPENDENT override — the tier's interpretation
 * can differ from the base (an asymmetric Chronometer tier on a symmetric base,
 * or vice versa). Empty inputs / Inherit fall through to the base value.
 *
 * Exported for reuse by the ref-editor per-ref override UI (Phase 3a), which
 * layers the same accuracy override on top of a ref's `calibreCache`.
 */
export function AccuracyOverrideGroup({
  baseData,
  overrides,
  onChange,
  overrideLabel = 'base',
}: {
  baseData: CalibreData;
  overrides: CalibreTierOverrides;
  onChange: <K extends keyof CalibreTierOverrides>(
    key: K,
    value: CalibreTierOverrides[K],
  ) => void;
  overrideLabel?: string;
}) {
  const symOverride = overrides.accuracySymmetric;
  // Which interpretation the inputs are labelled under: the override if set,
  // otherwise the base's, otherwise asymmetric (the default).
  const effectiveSym = symOverride ?? baseData.accuracySymmetric ?? false;
  const numValue = (v: number | undefined) => (v === undefined ? '' : String(v));
  const parseNum = (raw: string) =>
    raw === '' ? undefined : (() => {
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? undefined : n;
    })();
  const previewTier: CalibreTier = {
    id: '',
    name: '',
    overrides: cleanOverrides(overrides),
  };
  // The trio is one logical spec — "active" when the tier overrides ANY of the
  // three keys; reset clears all three at once (back to inherit).
  const accuracyActive =
    overrides.accuracySymmetric !== undefined ||
    overrides.accuracyLowerSecPerDay !== undefined ||
    overrides.accuracyUpperSecPerDay !== undefined;
  const baseAccuracy = formatAccuracy(
    baseData.accuracySymmetric,
    baseData.accuracyLowerSecPerDay,
    baseData.accuracyUpperSecPerDay,
  );
  const connector = overrideLabel === 'base' ? '·' : '←';

  return (
    <div className='md:col-span-2 flex items-start gap-2 border-t border-gray-200 pt-2 mt-1'>
      <div className='w-40 shrink-0'>
        <div className='text-sm text-gray-700'>Accuracy</div>
        {accuracyActive ? (
          <div className='text-[11px]'>
            <span className='font-semibold text-brand-700'>
              Override: {effectiveAccuracy(baseData, previewTier).display}
            </span>{' '}
            <span className='text-gray-400'>
              {connector} {overrideLabel}: {baseAccuracy}
            </span>
          </div>
        ) : (
          <div className='text-[11px] text-gray-400'>
            {overrideLabel}: {baseAccuracy}
          </div>
        )}
      </div>
      <div className='flex-1 space-y-1.5'>
        <select
          value={symOverride === undefined ? '' : symOverride ? 'true' : 'false'}
          onChange={(e) => {
            const v = e.target.value;
            onChange('accuracySymmetric', v === '' ? undefined : v === 'true');
          }}
          className={inputClass}
        >
          <option value=''>
            Inherit ({baseData.accuracySymmetric ? 'Symmetric ±' : 'Asymmetric'})
          </option>
          <option value='true'>Symmetric ±</option>
          <option value='false'>Asymmetric (signed)</option>
        </select>
        <div className='grid grid-cols-2 gap-2'>
          <div>
            <label className='block text-[11px] text-gray-500 mb-0.5'>
              {effectiveSym ? 'Best-case spread' : 'Lower (signed)'}
            </label>
            <input
              type='number'
              value={numValue(overrides.accuracyLowerSecPerDay)}
              onChange={(e) =>
                onChange('accuracyLowerSecPerDay', parseNum(e.target.value))
              }
              className={inputClass}
              placeholder='Inherit'
            />
          </div>
          <div>
            <label className='block text-[11px] text-gray-500 mb-0.5'>
              {effectiveSym ? 'Worst-case spread' : 'Upper (signed)'}
            </label>
            <input
              type='number'
              value={numValue(overrides.accuracyUpperSecPerDay)}
              onChange={(e) =>
                onChange('accuracyUpperSecPerDay', parseNum(e.target.value))
              }
              className={inputClass}
              placeholder='Inherit'
            />
          </div>
        </div>
        <div className='text-[11px] text-gray-400'>
          effective:{' '}
          <span className='font-medium text-gray-600'>
            {effectiveAccuracy(baseData, previewTier).display}
          </span>
        </div>
      </div>
      {accuracyActive && (
        <ResetOverrideButton
          label={overrideLabel}
          onClick={() => {
            // Accuracy is one logical spec — clear all three keys together.
            onChange('accuracySymmetric', undefined);
            onChange('accuracyLowerSecPerDay', undefined);
            onChange('accuracyUpperSecPerDay', undefined);
          }}
        />
      )}
    </div>
  );
}

/**
 * The two-column grid of override controls (one row per overridable field,
 * accuracy grouped) shown against a `baseData` spec. Each row shows the base
 * value it would replace and a tri-state control (Inherit / set). Extracted so
 * the tier editor and the ref-editor per-ref override UI (Phase 3a) render an
 * identical control surface — the ref editor passes its `calibreCache`
 * (projected onto a `CalibreData`) as `baseData`.
 */
export function OverrideFieldsGrid({
  baseData,
  overrides,
  onChange,
  overrideLabel = 'base',
}: {
  baseData: CalibreData;
  overrides: CalibreTierOverrides;
  onChange: <K extends keyof CalibreTierOverrides>(
    key: K,
    value: CalibreTierOverrides[K],
  ) => void;
  /**
   * Noun for the value an override replaces, shown in the per-row base/delta
   * subtext. Defaults to `base` (the tier editor, where the row edits a tier
   * over the calibre's own base spec). The ref-editor override grid passes
   * `calibre` — there we're layering a per-ref value over the linked calibre.
   * The delta connector follows suit: `←` when overriding something external
   * (calibre), `·` for the tier-over-base case.
   */
  overrideLabel?: string;
}) {
  const connector = overrideLabel === 'base' ? '·' : '←';
  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2'>
      {OVERRIDABLE_FIELDS.filter((f) => !isAccuracyKey(f.key)).map((f) => {
        const base = baseValueFor(baseData, f.key);
        const active = overrides[f.key] !== undefined;
        return (
          <div
            key={f.key}
            className={`flex items-center gap-2 min-w-0 rounded-md px-1 py-0.5 ${
              active ? 'bg-brand-50' : ''
            }`}
          >
            <div className='w-40 shrink-0'>
              <div className='text-sm text-gray-700'>{f.label}</div>
              {active ? (
                <div className='text-[11px]'>
                  <span className='font-semibold text-brand-700'>
                    Override: {displayOverrideValue(overrides[f.key])}
                  </span>{' '}
                  <span className='text-gray-400'>
                    {connector} {overrideLabel}: {displayOverrideValue(base)}
                  </span>
                </div>
              ) : (
                <div className='text-[11px] text-gray-400'>
                  {overrideLabel}: {displayOverrideValue(base)}
                </div>
              )}
            </div>
            <div className='flex-1 min-w-0'>
              {f.kind === 'boolean' && (
                <BooleanOverride
                  value={overrides[f.key] as boolean | undefined}
                  onChange={(v) => onChange(f.key, v as never)}
                />
              )}
              {f.kind === 'lookup' && (
                <LookupFieldSelect
                  label=''
                  value={(overrides[f.key] as string | undefined) ?? ''}
                  lookupCollection={f.lookupCollection as string}
                  placeholder='Inherit'
                  onChange={(v) => onChange(f.key, (v || undefined) as never)}
                />
              )}
              {f.kind === 'multi-lookup' && (
                // An empty array is NOT the same as Inherit here: `[]` means
                // "this tier strips the base's list wholesale", an explicit
                // value the shared-types contract defines. So deselecting the
                // last chip leaves `[]` set rather than collapsing to undefined
                // — clearing back to Inherit is the reset button's job.
                <LookupMultiSelect
                  label=''
                  values={(overrides[f.key] as string[] | undefined) ?? []}
                  lookupCollection={f.lookupCollection as string}
                  onChange={(v) => onChange(f.key, v as never)}
                />
              )}
              {f.kind === 'text' && (
                <input
                  type='text'
                  value={(overrides[f.key] as string | undefined) ?? ''}
                  onChange={(e) =>
                    onChange(f.key, (e.target.value || undefined) as never)
                  }
                  className={inputClass}
                  placeholder={f.placeholder ?? 'Inherit'}
                />
              )}
              {f.kind === 'number' && (
                <input
                  type='number'
                  value={
                    overrides[f.key] === undefined
                      ? ''
                      : String(overrides[f.key])
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    onChange(
                      f.key,
                      (raw === '' ? undefined : parseInt(raw, 10)) as never,
                    );
                  }}
                  className={inputClass}
                  placeholder='Inherit'
                />
              )}
            </div>
            {active && (
              <ResetOverrideButton
                label={overrideLabel}
                onClick={() => onChange(f.key, undefined as never)}
              />
            )}
          </div>
        );
      })}
      <AccuracyOverrideGroup
        baseData={baseData}
        overrides={overrides}
        onChange={onChange}
        overrideLabel={overrideLabel}
      />
    </div>
  );
}

/**
 * Small trailing "reset to <label>" icon-button for an active override row —
 * clears the override (equivalent to selecting Inherit) in one click. Only
 * rendered when the field is currently overridden.
 */
function ResetOverrideButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      title={`Reset to ${label} (inherit)`}
      aria-label={`Reset to ${label}`}
      className='shrink-0 rounded p-1 text-gray-400 hover:bg-brand-100 hover:text-brand-700'
    >
      <svg
        className='h-3.5 w-3.5'
        fill='none'
        stroke='currentColor'
        viewBox='0 0 24 24'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
        />
      </svg>
    </button>
  );
}

/**
 * The add/edit form for a single tier, rendered in its own modal.
 *
 * Editing a tier in a dedicated modal (rather than inline in the parent calibre
 * modal) is what keeps mid-edit tier state from being silently dropped when the
 * parent's Save fires: the parent's `tiers[]` only changes when this modal's own
 * Save commits, so an open-but-uncommitted tier form never leaks into — or is
 * lost from — the parent's committed list. Cancel/Escape discard.
 */
/**
 * A deferred vocabulary decision surfaced when a member calibre's tier name is
 * off-vocabulary. `didYouMean` holds a close canonical match to offer; `typed`
 * is always the user's raw entry.
 */
type PendingVocab =
  | { kind: 'didYouMean'; typed: string; suggestion: string; why: 'spelling' | 'close' }
  | { kind: 'destination'; typed: string };

function TierFormModal({
  initial,
  mode,
  baseData,
  existingNames,
  tierVocabulary,
  onAddTierName,
  brandOptions,
  defaultBrandId,
  media,
  onCancel,
  onSave,
}: {
  initial: CalibreTier | null;
  /**
   * Which job the form is doing. A CLONE arrives with `initial` populated (the
   * copy's overrides and scope) but is still a new tier, so it cannot be
   * inferred from `initial` the way add and edit can.
   */
  mode: 'add' | 'edit' | 'clone';
  baseData: CalibreData;
  existingNames: string[];
  tierVocabulary?: CalibreTierVocabulary;
  onAddTierName?: (destination: 'manufacturer' | 'family', name: string) => Promise<void>;
  brandOptions?: Array<{ id: string; name: string }>;
  defaultBrandId?: string;
  media?: TierMediaProps;
  onCancel: () => void;
  onSave: (tier: CalibreTier) => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [overrides, setOverrides] = useState<CalibreTierOverrides>(
    initial?.overrides ?? {},
  );
  // The tier model (James, 2026-10-29): manufacturer and family Tier Names are
  // GENERIC grade names only; a brand-specific tier lives on the calibre alone.
  // So the General / Brand-specific toggle depends on the NAME, in every mode:
  // a name that is a listed generic grade (or a spelling of one) is LINKED —
  // locked general, no toggle, no brand. Any other name may be either, and the
  // toggle shows. Add starts general; edit and clone start from the tier's own
  // scope (a clone of a brand tier is a starting point for another one).
  // Back-compat: a tier saved before tierType existed reads as 'general'.
  // ONCE CREATED, A TIER'S TYPE IS LOCKED (James, 2026-10-29): edit never
  // shows the toggle; the wrong type is fixed by deleting and recreating (or
  // Clone, which keeps the overrides). So only add and clone choose a type.
  const typeLocked = mode === 'edit';
  const [tierType, setTierType] = useState<'general' | 'brand-specific'>(
    mode !== 'add' && initial?.tierType === 'brand-specific' ? 'brand-specific' : 'general',
  );
  const [brandId, setBrandId] = useState<string>(
    initial?.brandId ?? '',
  );
  const [brandSearch, setBrandSearch] = useState('');
  const [manufacturers, setManufacturers] = useState({
    manufacturerId: initial?.manufacturerId ?? '',
    additionalManufacturerIds: initial?.additionalManufacturerIds ?? [],
  });
  const [images, setImages] = useState<CalibreImage[]>(initial?.images ?? []);
  // Memoized because `useBrandSearch` keys its result off the array identity,
  // and `brandOptions` is optional so the `?? []` would otherwise be a new empty
  // array on every render.
  const brands = useMemo(() => brandOptions ?? [], [brandOptions]);
  // Algolia-backed (typo-tolerant, prefix, accent-folded, matches alternative
  // names / abbreviations / parent org), falling back to the previous
  // name-or-id substring filter when Algolia is unreachable.
  const { results: filteredBrands } = useBrandSearch(brands, brandSearch);

  // When the user first switches a tier to brand-specific and it has no brand
  // yet, seed the calibre's own brand as the sensible default.
  const selectBrandSpecific = () => {
    setTierType('brand-specific');
    if (!brandId && defaultBrandId) setBrandId(defaultBrandId);
  };
  // A deferred vocabulary prompt (did-you-mean modal), or null when none is open.
  const [pendingVocab, setPendingVocab] = useState<PendingVocab | null>(null);
  const [addingVocab, setAddingVocab] = useState(false);
  const vocabListId = useId();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const setOverride = <K extends keyof CalibreTierOverrides>(
    key: K,
    value: CalibreTierOverrides[K],
  ) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  };

  const trimmedName = name.trim();
  const nameClash = existingNames.some(
    (n) => n.toLowerCase() === trimmedName.toLowerCase(),
  );

  // The names on offer: the generic grades only, from the manufacturer and the
  // family. Brand tier names are never suggested — they belong to one calibre.
  const groups = useMemo(
    () =>
      tierVocabulary
        ? tierNameOptions(tierVocabulary)
        : { manufacturer: [] as TierNameOption[], family: [] as TierNameOption[] },
    [tierVocabulary],
  );
  const allOptions = useMemo(() => [...groups.manufacturer, ...groups.family], [groups]);
  // LINKED: the name is a listed generic grade, or a spelling of one.
  const linkedTo = useMemo(() => {
    const res = resolveTypedTierName(trimmedName, allOptions);
    return res.kind === 'exact' ? res.name : res.kind === 'spelling' ? res.canonical : null;
  }, [trimmedName, allOptions]);
  const linked = linkedTo !== null;
  // On edit the stored type stands, whatever the name now says.
  const isBrandTier = typeLocked ? tierType === 'brand-specific' : !linked && tierType === 'brand-specific';
  // A brand-specific tier MUST carry a brand (enforced here, not in the type).
  const brandMissing = isBrandTier && !brandId;
  // A brand tier can never take a grade's name (D7) — on edit that is refused
  // rather than silently turning the tier general.
  const brandTakesGradeName = typeLocked && isBrandTier && linked;
  const canSave = trimmedName.length > 0 && !nameClash && !brandMissing && !brandTakesGradeName;
  const hasVocab = allOptions.length > 0;
  const manufacturerLabel = tierVocabulary?.manufacturerName || 'Manufacturer';
  const familyLabel = tierVocabulary?.familyName ? `${tierVocabulary.familyName} family` : undefined;

  /** Build + emit the tier with a finalized name (closes the form). */
  const commit = (finalName: string) => {
    const { manufacturerId, additionalManufacturerIds } = manufacturers;
    // A name that resolves to a listed grade is always general, whatever the
    // toggle last said (a did-you-mean can land here on a canonical name).
    const savedType = typeLocked
      ? tierType
      : resolveTypedTierName(finalName, allOptions).kind === 'none' && tierType === 'brand-specific'
        ? 'brand-specific'
        : 'general';
    onSave({
      // Start from the tier as loaded so nothing the form doesn't render is
      // lost on save — rebuilding from scratch here used to drop the
      // manufacturers and images a reclassify carry-over (#680) wrote.
      ...(initial ?? {}),
      id: initial?.id ?? newTierId(),
      name: finalName,
      manufacturerId: manufacturerId.trim() || undefined,
      additionalManufacturerIds: additionalManufacturerIds.length > 0 ? additionalManufacturerIds : undefined,
      images: images.length > 0 ? images : undefined,
      description: description.trim() || undefined,
      tierType: savedType,
      // Brand only travels with a brand-specific tier; general tiers stay
      // brand-less (a general grade belongs to the manufacturer, not a brand).
      brandId: savedType === 'brand-specific' ? brandId : undefined,
      overrides: cleanOverrides(overrides),
    });
  };

  /**
   * Add-as-new for a GENERIC name: persist it to the chosen list, then commit
   * the tier with that name. A persist failure aborts before commit, so the tier
   * never carries a name whose save failed. 'calibre' keeps it on this calibre
   * only and writes no list.
   */
  const persistAndCommit = async (finalName: string, destination: 'manufacturer' | 'family' | 'calibre') => {
    if (destination !== 'calibre' && onAddTierName) {
      setAddingVocab(true);
      try {
        await onAddTierName(destination, finalName);
      } catch (err) {
        window.alert(
          `Failed to add "${finalName}" to the ${destination === 'manufacturer' ? manufacturerLabel : (familyLabel ?? 'family')} tier names. ${
            err instanceof Error ? err.message : 'Please try again.'
          }`,
        );
        return;
      } finally {
        setAddingVocab(false);
      }
    }
    setPendingVocab(null);
    commit(finalName);
  };

  const handleSave = () => {
    if (!canSave) return;
    // A brand-specific tier is free text on this calibre alone: no did-you-mean
    // against the grades (a linked name never reaches here — it is locked
    // general) and no "add to the manufacturer / family list" prompt.
    if (isBrandTier) {
      commit(trimmedName);
      return;
    }
    const res = resolveTypedTierName(trimmedName, allOptions);
    if (res.kind === 'exact') {
      commit(res.name);
      return;
    }
    if (res.kind === 'spelling') {
      setPendingVocab({ kind: 'didYouMean', typed: trimmedName, suggestion: res.canonical, why: 'spelling' });
      return;
    }
    const close = hasVocab ? findClosestMatch(trimmedName, allOptions.map((o) => o.name)) : null;
    if (close) {
      setPendingVocab({ kind: 'didYouMean', typed: trimmedName, suggestion: close, why: 'close' });
      return;
    }
    if (onAddTierName) {
      setPendingVocab({ kind: 'destination', typed: trimmedName });
      return;
    }
    commit(trimmedName);
  };

  // Enter in a single-line text field commits (mirrors typical form UX); Escape
  // is handled by the Modal itself and routes to onCancel (discard).
  const handleTextKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={mode === 'edit' ? 'Edit tier' : mode === 'clone' ? 'Clone tier' : 'Add tier'}
      size='xl'
    >
      <div className='space-y-3'>
        {/* Did-you-mean: the typed name is another spelling of a listed name,
            or close to one. Offer the listed name, or adding the typed one. */}
        {pendingVocab?.kind === 'didYouMean' && (
          <div className='border border-amber-300 bg-amber-50 rounded-lg p-3'>
            <p className='text-sm text-amber-800'>
              <span className='font-semibold'>"{pendingVocab.typed}"</span>{' '}
              {pendingVocab.why === 'spelling' ? 'is another spelling of' : "isn't a listed tier name. Did you mean"}{' '}
              <span className='font-semibold'>"{pendingVocab.suggestion}"</span>
              {pendingVocab.why === 'spelling' ? '. Use the listed name?' : '?'}
            </p>
            <div className='flex flex-wrap items-center gap-2 mt-2'>
              <Button
                size='sm'
                onClick={() => {
                  const { suggestion } = pendingVocab;
                  setPendingVocab(null);
                  commit(suggestion);
                }}
                disabled={addingVocab}
              >
                Use "{pendingVocab.suggestion}"
              </Button>
              {pendingVocab.why === 'close' && (
                <Button
                  size='sm'
                  variant='secondary'
                  onClick={() =>
                    onAddTierName
                      ? setPendingVocab({ kind: 'destination', typed: pendingVocab.typed })
                      : commit(pendingVocab.typed)
                  }
                  disabled={addingVocab}
                >
                  Add "{pendingVocab.typed}" as new…
                </Button>
              )}
              <Button
                size='sm'
                variant='ghost'
                onClick={() => setPendingVocab(null)}
                disabled={addingVocab}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {/* Add-as-new asks WHERE the name belongs: the manufacturer (every
            calibre it makes), this calibre's family, or this calibre only. */}
        {pendingVocab?.kind === 'destination' && (
          <div className='border border-blue-300 bg-blue-50 rounded-lg p-3'>
            <p className='text-sm text-blue-900'>
              <span className='font-semibold'>"{pendingVocab.typed}"</span> is a new tier name. Where should it be
              added?
            </p>
            <div className='flex flex-wrap items-center gap-2 mt-2'>
              <Button
                size='sm'
                onClick={() => void persistAndCommit(pendingVocab.typed, 'manufacturer')}
                loading={addingVocab}
              >
                {manufacturerLabel}&rsquo;s tier names
              </Button>
              {familyLabel && (
                <Button
                  size='sm'
                  variant='secondary'
                  onClick={() => void persistAndCommit(pendingVocab.typed, 'family')}
                  disabled={addingVocab}
                >
                  {familyLabel} only
                </Button>
              )}
              <Button
                size='sm'
                variant='secondary'
                onClick={() => void persistAndCommit(pendingVocab.typed, 'calibre')}
                disabled={addingVocab}
              >
                This calibre only
              </Button>
              <Button
                size='sm'
                variant='ghost'
                onClick={() => setPendingVocab(null)}
                disabled={addingVocab}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
          <div>
            <label className='block text-xs font-medium text-gray-600 mb-1'>
              Tier name<span className='text-red-500'> *</span>
            </label>
            <input
              ref={nameInputRef}
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleTextKeyDown}
              className={inputClass}
              placeholder='e.g., Elaboré, Top, Chronometer'
              list={hasVocab ? vocabListId : undefined}
              autoFocus
            />
            {/* Keep the datalist too (belt & braces) — desktop Chrome users get
                the native autocomplete; the visible chip row below is the
                cross-platform picker that actually works on iOS Safari, where
                <datalist> only surfaces in the keyboard's QuickType bar. */}
            {hasVocab && (
              <datalist id={vocabListId}>
                {allOptions.map((o) => (
                  <option key={`${o.source}:${o.name}`} value={o.name} />
                ))}
              </datalist>
            )}
            {hasVocab && (
              <>
                <p className='text-[11px] text-gray-400 mt-1'>
                  {isBrandTier
                    ? 'Pick one of this brand’s listed tier names, or type one.'
                    : 'Pick a listed tier name, or type a new one.'}
                </p>
                {/* Visible, tappable chip picker (iOS-friendly replacement for
                    the native <datalist> dropdown), grouped by source.
                    Already-used names are greyed but stay clickable. */}
                {(
                  [
                    ['manufacturer', `From ${manufacturerLabel}`, groups.manufacturer],
                    ['family', familyLabel ? `From ${familyLabel}` : 'From the family', groups.family],
                  ] as const
                ).map(([key, label, opts]) =>
                  opts.length === 0 ? null : (
                    <div key={key} className='mt-1.5'>
                      <div className='text-[10px] uppercase tracking-wide text-gray-400'>{label}</div>
                      <div className='flex flex-wrap gap-1.5 mt-0.5'>
                        {opts.map((o) => {
                          const isSelected = trimmedName.toLowerCase() === o.name.toLowerCase();
                          const isUsed = existingNames.some((e) => e.toLowerCase() === o.name.toLowerCase());
                          return (
                            <button
                              key={o.name}
                              type='button'
                              onClick={() => {
                                setName(o.name);
                                nameInputRef.current?.focus();
                              }}
                              title={[
                                isUsed ? 'Already used.' : '',
                                o.akas.length > 0 ? `Also: ${o.akas.join(', ')}` : '',
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              aria-pressed={isSelected}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors cursor-pointer max-w-full ${
                                isSelected
                                  ? 'bg-brand-600 text-white hover:bg-brand-700'
                                  : isUsed
                                    ? 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                    : key === 'manufacturer'
                                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                      : 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                              }`}
                            >
                              <span className='truncate'>{o.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ),
                )}
              </>
            )}
            {nameClash && (
              <p className='text-xs text-red-600 mt-1'>
                A tier with this name already exists.
              </p>
            )}
          </div>
          <div>
            <label className='block text-xs font-medium text-gray-600 mb-1'>
              Description
            </label>
            <input
              type='text'
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={handleTextKeyDown}
              className={inputClass}
              placeholder='Optional — what makes this tier distinct'
            />
          </div>
        </div>

        {/* Tier type — hidden for a LINKED name (a listed grade: always
            general), shown for any other name, in add, clone and edit alike. */}
        {typeLocked && (
          <p className='text-[11px] text-gray-500'>
            {isBrandTier ? (
              <>
                Brand-specific tier
                {brandId ? (
                  <> for <span className='font-medium'>{brands.find((b) => b.id === brandId)?.name ?? brandId}</span></>
                ) : null}
                .
              </>
            ) : (
              'General tier.'
            )}{' '}
            A tier&rsquo;s type can&rsquo;t be changed once it exists — delete it and add it again (Clone keeps its
            overrides).
            {brandTakesGradeName && (
              <span className='block text-red-600 mt-1'>
                &ldquo;{linkedTo}&rdquo; is a listed grade — a brand tier can&rsquo;t take a grade&rsquo;s name.
              </span>
            )}
          </p>
        )}
        {!typeLocked && linked && (
          <p className='text-[11px] text-gray-500'>
            &ldquo;{linkedTo}&rdquo; is a {manufacturerLabel}
            {familyLabel ? ` / ${familyLabel}` : ''} grade, so this is a general tier — it applies to every brand
            using this calibre.
          </p>
        )}
        {!typeLocked && !linked && (
        <div>
          <label className='block text-xs font-medium text-gray-600 mb-1'>
            Tier type
          </label>
          <div className='inline-flex rounded-lg border border-gray-300 overflow-hidden'>
            <button
              type='button'
              onClick={() => {
                // Back to general clears the brand: a general tier has none.
                setTierType('general');
                setBrandId('');
              }}
              aria-pressed={tierType === 'general'}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                tierType === 'general'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              General
            </button>
            <button
              type='button'
              onClick={selectBrandSpecific}
              aria-pressed={tierType === 'brand-specific'}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-300 ${
                tierType === 'brand-specific'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Brand-specific
            </button>
          </div>
          <p className='text-[11px] text-gray-400 mt-1'>
            {tierType === 'general'
              ? 'Applies across every brand that uses this calibre.'
              : 'A modification one brand applies on the base movement — scoped to that brand’s references, on this calibre only.'}
          </p>

          {isBrandTier && (
            <div className='mt-2'>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Brand<span className='text-red-500'> *</span>
              </label>
              <input
                type='text'
                value={brandSearch}
                onChange={(e) => setBrandSearch(e.target.value)}
                placeholder='Search brands…'
                className={`${inputClass} mb-1.5`}
              />
              <select
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
                className={inputClass}
              >
                <option value=''>
                  {brands.length === 0
                    ? 'No brands loaded'
                    : `Select a brand… (${filteredBrands.length} match)`}
                </option>
                {filteredBrands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {brandMissing && (
                <p className='text-xs text-red-600 mt-1'>
                  A brand-specific tier requires a brand.
                </p>
              )}
            </div>
          )}
        </div>
        )}

        <div>
          <div className='text-xs font-medium text-gray-500 uppercase mb-1.5'>
            Overrides
          </div>
          <p className='text-xs text-gray-400 mb-2'>
            Leave a field on <em>Inherit</em> / blank to use the base calibre
            value. Only fields you set here override the base.
          </p>
          <OverrideFieldsGrid
            baseData={baseData}
            overrides={overrides}
            onChange={setOverride}
          />
        </div>

        {media && (
          <div className='space-y-3 border-t border-gray-200 pt-3'>
            <div className='text-xs font-medium text-gray-500 uppercase'>
              Manufacturers &amp; images
            </div>
            <TierManufacturerFields
              manufacturerId={manufacturers.manufacturerId}
              additionalManufacturerIds={manufacturers.additionalManufacturerIds}
              onChange={setManufacturers}
              options={media.manufacturerOptions}
              loading={media.manufacturersLoading}
            />
            <TierImagesField
              images={images}
              onChange={setImages}
              uploadImage={media.uploadImage}
              resolveImageUrl={media.resolveImageUrl}
              fetchImage={media.fetchImage}
              defaultSource={media.defaultImageSource}
              maxImages={media.maxImages}
            />
          </div>
        )}

        <div className='flex justify-end gap-2 border-t border-gray-200 pt-3'>
          <Button variant='secondary' size='sm' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant='primary'
            size='sm'
            onClick={handleSave}
            disabled={!canSave}
          >
            {mode === 'edit' ? 'Update tier' : 'Add tier'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * CRUD editor for a calibre's `tiers[]`. Lists tiers with an override count,
 * and add/edit/delete controls. Deleting confirms (destructive) and warns that
 * refs pointing at the tier via `calibreTierId` will be orphaned. The `tiers`
 * array is owned by the parent (the calibre modal) and persisted through the
 * normal calibre save flow — this component never writes to Firestore itself.
 */
export function CalibreTiersEditor({
  tiers,
  onChange,
  baseData,
  baseTierName,
  onBaseTierNameChange,
  baseSpec,
  tierVocabulary,
  onAddTierName,
  brandOptions,
  defaultBrandId,
  media,
}: CalibreTiersEditorProps) {
  // null = closed, '' = adding, CLONE_EDIT_KEY = cloning, otherwise the id being edited
  const [editing, setEditing] = useState<string | null>(null);

  // Generic names on offer, for the "rename to the listed name" hint on
  // general-tier rows ("Special (Elaboré)" → "Elaboré").
  const genericOptions = useMemo(() => {
    if (!tierVocabulary) return [];
    const g = tierNameOptions(tierVocabulary);
    return [...g.manufacturer, ...g.family];
  }, [tierVocabulary]);
  const renameFor = (tier: CalibreTier): string | null => {
    if (tier.tierType === 'brand-specific') return null;
    const to = canonicalRenameFor(tier.name, genericOptions);
    // Never rename onto a name another tier already has.
    if (!to || tiers.some((t) => t.id !== tier.id && t.name.trim().toLowerCase() === to.toLowerCase())) return null;
    return to;
  };
  const handleRename = (tier: CalibreTier, to: string) => {
    onChange(tiers.map((t) => (t.id === tier.id ? { ...t, name: to } : t)));
  };
  /**
   * The pre-filled copy a Clone action produced — a real tier object with a
   * fresh id and no name, waiting to be named and saved. Held separately from
   * `tiers` so an abandoned clone never reaches the calibre's list.
   */
  const [cloneSeed, setCloneSeed] = useState<CalibreTier | null>(null);

  const editingTier =
    editing && editing !== '' && editing !== CLONE_EDIT_KEY
      ? tiers.find((t) => t.id === editing) ?? null
      : null;
  const cloning = editing === CLONE_EDIT_KEY;

  const closeForm = () => {
    setEditing(null);
    setCloneSeed(null);
  };

  /**
   * Open the form pre-filled with a copy of `tier`: same scope, same overrides,
   * no name. Saving appends it, because the copy carries a fresh id — the
   * source is never touched, whatever the curator then changes.
   */
  const handleClone = (tier: CalibreTier) => {
    setCloneSeed(cloneTier(tier, newTierId()));
    setEditing(CLONE_EDIT_KEY);
  };

  const handleSaveTier = (tier: CalibreTier) => {
    const idx = tiers.findIndex((t) => t.id === tier.id);
    if (idx >= 0) {
      const next = [...tiers];
      next[idx] = tier;
      onChange(next);
    } else {
      onChange([...tiers, tier]);
    }
    closeForm();
  };

  /**
   * Reorder a tier by swapping it with its neighbour. The `tiers[]` array's
   * natural order IS the persisted order (no schema field), so a swap + onChange
   * is all that's needed; every other tier's state is preserved. Guards against
   * out-of-bounds so the top row's up / bottom row's down are no-ops.
   */
  const handleMove = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= tiers.length) return;
    const next = [...tiers];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const handleDelete = (tier: CalibreTier) => {
    const ok = window.confirm(
      `Delete tier "${tier.name}"?\n\n` +
        'This cannot be undone. Any references that point at this tier ' +
        '(via calibreTierId) will fall back to the base calibre spec.',
    );
    if (!ok) return;
    onChange(tiers.filter((t) => t.id !== tier.id));
    if (editing === tier.id) closeForm();
  };

  /**
   * Widen a brand-specific tier to a general one: drop the brand scope so every
   * brand using this calibre — and every consumer that reads through
   * `tiersForBrand`, AI enrichment included — can see it.
   *
   * Purely a local edit; the calibre's normal save persists it, and
   * `sanitizeTierForWrite` at that boundary is what actually strips `brandId`
   * (it refuses to carry a brand on a general tier). Nothing server-side needs
   * to run: references point at the tier by `calibreTierId`, and the id does not
   * change, so no reference is orphaned or re-pointed.
   */
  const existingNames = (excludeId?: string) =>
    tiers.filter((t) => t.id !== excludeId).map((t) => t.name);

  return (
    <div className='border rounded-lg p-3 bg-gray-50 space-y-3'>
      <div className='flex items-center justify-between'>
        <label className='text-xs font-medium text-gray-600 uppercase'>
          Tiers ({tiers.length})
        </label>
        {editing === null && (
          <Button variant='secondary' size='sm' onClick={() => setEditing('')}>
            + Add tier
          </Button>
        )}
      </div>

      <p className='text-xs text-gray-400'>
        Tier variants of this calibre (e.g. base / Elaboré / Top / Chronometer).
        Each tier overrides selected spec fields; identity fields (name,
        manufacturer, base movement, movement type) are never overridable.
      </p>

      {tiers.length > 0 && (
        <div className='space-y-1.5'>
          {/* Synthetic "Base" row: the calibre's own spec, heading the tier
              list. Only shown when there ARE tiers to head. The display name is
              editable (bound to baseTierName); every other value is a read-only
              readout of the live base spec — no override / reorder / delete
              controls and no override-count badge, since it isn't a real tier.
              Muted background + dashed border + italic label + "(base)" suffix
              distinguish it from the normal tier rows below. */}
          <div className='flex flex-col gap-2 bg-gray-100 border border-dashed border-gray-300 rounded px-3 py-2'>
            <div className='flex items-center gap-2'>
              <input
                type='text'
                value={baseTierName}
                onChange={(e) => onBaseTierNameChange(e.target.value)}
                placeholder='Base'
                aria-label='Base tier display name'
                className='flex-1 min-w-0 italic text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded px-2 py-1'
              />
              <span className='shrink-0 text-[11px] italic text-gray-400'>
                (base)
              </span>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5'>
              {OVERRIDABLE_FIELDS.filter((f) => !isAccuracyKey(f.key)).map((f) => (
                <div
                  key={f.key}
                  className='flex items-baseline justify-between gap-2 text-[11px]'
                >
                  <span className='text-gray-500'>{f.label}</span>
                  <span className='font-medium text-gray-600'>
                    {displayOverrideValue(baseValueFor(baseSpec, f.key))}
                  </span>
                </div>
              ))}
              {/* Accuracy is one logical spec across three keys — render it via
                  the shared formatAccuracy helper, matching the tier form. */}
              <div className='flex items-baseline justify-between gap-2 text-[11px]'>
                <span className='text-gray-500'>Accuracy</span>
                <span className='font-medium text-gray-600'>
                  {formatAccuracy(
                    baseSpec.accuracySymmetric,
                    baseSpec.accuracyLowerSecPerDay,
                    baseSpec.accuracyUpperSecPerDay,
                  )}
                </span>
              </div>
            </div>
          </div>
          {tiers.map((tier, index) => (
            <div
              key={tier.id}
              className='flex items-center gap-2 bg-white border border-gray-200 rounded px-3 py-2'
            >
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className='text-sm font-medium text-gray-800'>
                    {tier.name}
                  </span>
                  <span className='text-[11px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5'>
                    {countOverrides(tier)} override
                    {countOverrides(tier) === 1 ? '' : 's'}
                  </span>
                  {tier.tierType === 'brand-specific' && (
                    <span className='text-[11px] text-gray-500 bg-gray-100 rounded-full px-2 py-0.5'>
                      Brand-specific
                    </span>
                  )}
                  {(() => {
                    const to = renameFor(tier);
                    return to ? (
                      <button
                        type='button'
                        onClick={() => handleRename(tier, to)}
                        title='This name is another spelling of a listed tier name. Renaming keeps the tier, its id, overrides and images.'
                        className='text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 hover:bg-amber-100'
                      >
                        Listed as “{to}” — rename
                      </button>
                    ) : null;
                  })()}
                </div>
                {tier.description && (
                  <div className='text-xs text-gray-500 truncate'>
                    {tier.description}
                  </div>
                )}
              </div>
              <div className='flex shrink-0 items-center gap-1.5'>
                <div className='flex flex-col'>
                  <button
                    type='button'
                    onClick={() => handleMove(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${tier.name} up`}
                    title='Move up'
                    className='px-1.5 text-xs leading-none text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed'
                  >
                    ▲
                  </button>
                  <button
                    type='button'
                    onClick={() => handleMove(index, 1)}
                    disabled={index === tiers.length - 1}
                    aria-label={`Move ${tier.name} down`}
                    title='Move down'
                    className='px-1.5 text-xs leading-none text-gray-500 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed'
                  >
                    ▼
                  </button>
                </div>
                <button
                  type='button'
                  onClick={() => setEditing(tier.id)}
                  className='rounded border border-brand-300 px-2 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-50'
                >
                  Edit
                </button>
                {/* A starting point for another tier on this calibre — general or
                    brand-specific, decided by its name in the form. */}
                <button
                  type='button'
                  onClick={() => handleClone(tier)}
                  title='Start a new tier on this calibre with this one’s overrides, ready to rename.'
                  className='rounded border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-50'
                >
                  Clone
                </button>
                <button
                  type='button'
                  onClick={() => handleDelete(tier)}
                  className='rounded border border-red-300 px-2 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50'
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tiers.length === 0 && editing === null && (
        <p className='text-xs text-gray-400 italic'>No tiers defined.</p>
      )}

      {editing !== null && (
        // key by the edit target ('' for add, else the tier id) so switching
        // straight from one tier's editor to another remounts with fresh state
        // rather than retaining the previous tier's field values.
        <TierFormModal
          key={editing}
          initial={cloning ? cloneSeed : editingTier}
          mode={cloning ? 'clone' : editingTier ? 'edit' : 'add'}
          baseData={baseData}
          // A clone excludes nothing: its name must differ from every existing
          // tier, the one it was copied from included.
          existingNames={existingNames(cloning ? undefined : editingTier?.id)}
          tierVocabulary={tierVocabulary}
          onAddTierName={onAddTierName}
          brandOptions={brandOptions}
          defaultBrandId={defaultBrandId}
          media={media}
          onCancel={closeForm}
          onSave={handleSaveTier}
        />
      )}
    </div>
  );
}
